import React, { useState } from 'react'
import PropTypes from 'prop-types'
import moment from 'moment-timezone'
import { OverlayTrigger, Tooltip } from 'react-bootstrap'
import { Button } from '@abroad/components'
import { DeleteConfirmationPrompt } from '../common'

const DateOverridesSection = ({
  overrides = [],
  onAddOverride,
  onRemoveOverride,
  isLoading,
  shouldShowTooltip = false,
}) => {
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [overrideToRemove, setOverrideToRemove] = useState(null)
  const formatDate = (dateMoment) => {
    if (!dateMoment || !dateMoment.isValid()) return ''
    return dateMoment.format('dddd, MMMM D, YYYY')
  }

  const formatTime = (dateMoment) => {
    if (!dateMoment || !dateMoment.isValid()) return ''
    const hour = dateMoment.hour()
    const minute = dateMoment.minute()
    const ampm = hour >= 12 ? 'pm' : 'am'
    const hour12 = hour % 12 || 12
    const mins = minute.toString().padStart(2, '0')
    return `${hour12}:${mins}${ampm}`
  }

  const getOverrideLines = () => {
    if (!Array.isArray(overrides) || overrides.length === 0) return []

    // Map each override to its display line with the override data
    const overrideLines = []

    overrides.forEach((override) => {
      // Check if override is in new format (start/end UTC timestamps with timezone)
      if (override.start && override.end && override.timezone) {
        const startUTC = moment.utc(override.start)
        const endUTC = moment.utc(override.end)
        const timezone = override.timezone
        const isActive = override.isActive !== false

        // Convert to the timezone specified in the override
        const startInTimezone = startUTC.tz(timezone)
        const endInTimezone = endUTC.tz(timezone)

        const dateStr = formatDate(startInTimezone)

        if (!isActive) {
          // Unavailable: mark the date as unavailable
          overrideLines.push({
            text: `${dateStr} Unavailable (All day)`,
            override: override,
          })
        } else {
          // Available: add time range
          const startTime = formatTime(startInTimezone)
          const endTime = formatTime(endInTimezone)
          overrideLines.push({
            text: `${dateStr} Available ${startTime} - ${endTime}`,
            override: override,
          })
        }
      } else if (override.startDate && override.endDate) {
        // Handle old format for backward compatibility
        const dateMoment = moment(override.startDate)
        const dateStr = formatDate(dateMoment)
        const isUnavailable = !override.isActive

        if (isUnavailable) {
          overrideLines.push({
            text: `${dateStr} Unavailable (All day)`,
            override: override,
          })
        } else if (override.timeRanges && override.timeRanges.length > 0) {
          // For old format, each time range in the override gets a line
          override.timeRanges.forEach((range) => {
            if (range.start && range.end) {
              const [startHour, startMinute] = range.start
                .split(':')
                .map(Number)
              const [endHour, endMinute] = range.end.split(':').map(Number)

              const startMoment = dateMoment
                .clone()
                .hour(startHour)
                .minute(startMinute || 0)
                .second(0)
                .millisecond(0)

              const endMoment = dateMoment
                .clone()
                .hour(endHour)
                .minute(endMinute || 0)
                .second(0)
                .millisecond(0)

              if (endMoment.isBefore(startMoment)) {
                endMoment.add(1, 'day')
              }

              const startTime = formatTime(startMoment)
              const endTime = formatTime(endMoment)
              overrideLines.push({
                text: `${dateStr} Available ${startTime} - ${endTime}`,
                override: override,
              })
            }
          })
        }
      }
    })

    // Sort by date and time
    overrideLines.sort((a, b) => {
      // Extract date from override for comparison
      let dateA, dateB

      if (a.override.start && a.override.end && a.override.timezone) {
        dateA = moment.utc(a.override.start).tz(a.override.timezone)
      } else if (a.override.startDate) {
        dateA = moment(a.override.startDate)
      } else {
        return 0
      }

      if (b.override.start && b.override.end && b.override.timezone) {
        dateB = moment.utc(b.override.start).tz(b.override.timezone)
      } else if (b.override.startDate) {
        dateB = moment(b.override.startDate)
      } else {
        return 0
      }

      if (dateA.isBefore(dateB)) return -1
      if (dateA.isAfter(dateB)) return 1
      return 0
    })

    return overrideLines
  }

  const getOverrideDisplayText = (override) => {
    if (!override) return ''

    // Check if override is in new format (start/end UTC timestamps with timezone)
    if (override.start && override.end && override.timezone) {
      const startUTC = moment.utc(override.start)
      const endUTC = moment.utc(override.end)
      const timezone = override.timezone
      const isActive = override.isActive !== false

      // Convert to the timezone specified in the override
      const startInTimezone = startUTC.tz(timezone)
      const endInTimezone = endUTC.tz(timezone)

      const dateStr = formatDate(startInTimezone)

      if (!isActive) {
        return `${dateStr} Unavailable (All day)`
      } else {
        const startTime = formatTime(startInTimezone)
        const endTime = formatTime(endInTimezone)
        return `${dateStr} Available ${startTime} - ${endTime}`
      }
    } else if (override.startDate && override.endDate) {
      // Handle old format for backward compatibility
      const dateMoment = moment(override.startDate)
      const dateStr = formatDate(dateMoment)
      const isUnavailable = !override.isActive

      if (isUnavailable) {
        return `${dateStr} Unavailable (All day)`
      } else if (override.timeRanges && override.timeRanges.length > 0) {
        // For old format with multiple time ranges, show the first one
        const firstRange = override.timeRanges[0]
        if (firstRange.start && firstRange.end) {
          const [startHour, startMinute] = firstRange.start
            .split(':')
            .map(Number)
          const [endHour, endMinute] = firstRange.end.split(':').map(Number)

          const startMoment = dateMoment
            .clone()
            .hour(startHour)
            .minute(startMinute || 0)
            .second(0)
            .millisecond(0)

          const endMoment = dateMoment
            .clone()
            .hour(endHour)
            .minute(endMinute || 0)
            .second(0)
            .millisecond(0)

          if (endMoment.isBefore(startMoment)) {
            endMoment.add(1, 'day')
          }

          const startTime = formatTime(startMoment)
          const endTime = formatTime(endMoment)
          return `${dateStr} Available ${startTime} - ${endTime}`
        }
      }
    }

    return 'this date override'
  }

  const handleRemoveClick = (override) => {
    setOverrideToRemove(override)
    setShowDeleteModal(true)
  }

  const handleConfirmRemove = () => {
    if (overrideToRemove && onRemoveOverride) {
      onRemoveOverride(overrideToRemove)
    }
    setShowDeleteModal(false)
    setOverrideToRemove(null)
  }

  const handleCancelRemove = () => {
    setShowDeleteModal(false)
    setOverrideToRemove(null)
  }

  const getConfirmationMessage = () => {
    if (!overrideToRemove) {
      return 'Are you sure you want to remove this date override?'
    }
    const overrideText = getOverrideDisplayText(overrideToRemove)
    return (
      <>
        Are you sure you want to remove this date override?
        <br />
        <br />
        <strong>{overrideText}</strong>
      </>
    )
  }

  return (
    <section className='availability-overrides-card'>
      <div className='override-content'>
        <div>
          <h6 className='sa2'>Date Overrides</h6>
          <p className='sa5 text-secondary'>
            Add dates when your availability changes from your daily hours.
          </p>
        </div>
        {overrides.length > 0 &&
          (() => {
            const overrideLines = getOverrideLines()
            return overrideLines.length > 0 ? (
              <div
                className='mt-3'
                style={{
                  border: '1px solid #000000',
                  borderRadius: '5px',
                  padding: '12px 17px',
                }}>
                {overrideLines.map((lineData, index) => (
                  <div
                    key={`override-line-${index}`}
                    className='sa2 d-block'
                    style={{
                      marginBottom:
                        index < overrideLines.length - 1 ? '0.656rem' : '0',
                    }}>
                    <span className='sa2'>{lineData.text}</span>
                    {onRemoveOverride && (
                      <span
                        className='text-danger ml-2'
                        onClick={() => handleRemoveClick(lineData.override)}
                        style={{
                          cursor: 'pointer',
                          fontWeight: 'normal',
                        }}
                        onMouseEnter={(e) => {
                          e.target.style.fontWeight = 'bold'
                        }}
                        onMouseLeave={(e) => {
                          e.target.style.fontWeight = 'normal'
                        }}>
                        Remove
                      </span>
                    )}
                  </div>
                ))}
              </div>
            ) : null
          })()}
        <div className='mt-3'>
          {shouldShowTooltip && !isLoading ? (
            <OverlayTrigger
              placement='top'
              overlay={
                <Tooltip id='add-override-tooltip'>
                  Please set availability above to use this feature
                </Tooltip>
              }>
              <span className='d-inline-block'>
                <Button
                  variant='outline-dark'
                  className='mr-3 sa6 py-13px px-4'
                  onClick={onAddOverride}
                  disabled={true}>
                  Add an override
                </Button>
              </span>
            </OverlayTrigger>
          ) : (
            <Button
              variant='outline-dark'
              className='mr-3 sa6 py-13px px-4'
              onClick={onAddOverride}
              disabled={isLoading || shouldShowTooltip}>
              Add an override
            </Button>
          )}
        </div>
      </div>
      <DeleteConfirmationPrompt
        showModal={showDeleteModal}
        setShowModal={setShowDeleteModal}
        isDeleteLoading={isLoading}
        handleDelete={handleConfirmRemove}
        message={getConfirmationMessage()}
        showCancelButton={true}
        onHide={handleCancelRemove}
        buttonConfirmText='Remove'
      />
    </section>
  )
}

DateOverridesSection.propTypes = {
  overrides: PropTypes.arrayOf(
    PropTypes.oneOfType([
      // New format
      PropTypes.shape({
        start: PropTypes.string,
        end: PropTypes.string,
        timezone: PropTypes.string,
        isActive: PropTypes.bool,
      }),
      // Old format (for backward compatibility)
      PropTypes.shape({
        startDate: PropTypes.string,
        endDate: PropTypes.string,
        isActive: PropTypes.bool,
        timeRanges: PropTypes.arrayOf(
          PropTypes.shape({
            start: PropTypes.string,
            end: PropTypes.string,
          }),
        ),
      }),
    ]),
  ),
  onAddOverride: PropTypes.func.isRequired,
  onRemoveOverride: PropTypes.func,
  isLoading: PropTypes.bool,
  shouldShowTooltip: PropTypes.bool,
}

export default DateOverridesSection
